import { api as emptyApi, Cache } from './emptyApi';
import type {
  ManageCustomersResponse,
  OrganizationCustomer,
  Subscription,
  SubscriptionRole,
} from '@/components/pages/ManageUsersPage/types';
import type { Statistic } from '@/components/pages/StatisticsPage/types';
import endpoints from '@/endpoints';
import type { SubscriptionOverviewResponse } from '@/components/pages/SubscriptionOverviewPage/types';
import { showMessage } from '../slices/message';
import getTranslation from '../../util/getTranslation';
import type { RootState } from '../types';
import type {
  CompanySearchArgs,
  CompanySearchResponse,
  CreateCompanyWithOrgUserRequest,
  CreateCompanyWithOrgUserResponse,
  CreateCompanyWithoutOrgUserResponse,
  CreateCompanyWithoutOrgUserRequest,
} from '@/types/company';
import type { CountryResponse } from '@/types/country';
import type {
  CreatePrivateUserRequest,
  CreatePrivateUserResponse,
} from '@/types/private';
import type {
  CreateStudentUserRequest,
  CreateStudentUserResponse,
  SchoolResponse,
} from '@/types/school';
import type {
  LanguageModel,
  NavigationLink,
  OrganizationInfo,
} from '@/components/global/Footer/types';
import type { SearchRequest, SearchResponse } from '@/types/search';
import { SearchResultStatus } from '@/types/search';
import { setActiveSort, setPage, updateFiltersFromServer } from '../slices/search';
import filterVisibleFilterItems from '@/util/filterVisibleFilterItems';

export interface FooterResponse {
  organizations: OrganizationInfo[];
  navigationLinks: NavigationLink[];
  languages: LanguageModel[];
  socialLinks: NavigationLink[];
}

export interface GetOrganizationStatisticsApiArgs {
  startDate: string;
  endDate: string;
}

export interface AddCustomersToSubscriptionsApiArgs {
  CustomerIds: number[];
  SubscriptionIds: string[];
}

export interface AddCustomersToSubscriptionsResponse {
  errors?: Error[];
  updatedCustomersCount: number;
}

export interface RemoveCustomersFromSubscriptionsApiArgs {
  CustomerIds: number[];
  SubscriptionIds: string[];
}

export interface RemoveCustomersFromSubscriptionsResponse {
  errors?: Error[];
  updatedCustomersCount: number;
}

export interface AssignSubscriptionCustomerRoleApiArgs {
  CustomerIds: Array<OrganizationCustomer['customerId']>;
  SubscriptionRoleId: SubscriptionRole['id'];
}

export interface AssignSubscriptionCustomerRoleResponse {
  errors?: Error[];
  updatedCustomersCount: number;
}

export interface RemoveCustomersFromOrganizationApiArgs {
  CustomerIds: Array<OrganizationCustomer['customerId']>;
}

export interface RemoveCustomersFromOrganizationResponse {
  errors?: Error[];
  removedCustomersCount: number;
}

export interface GetSubscriptionPriceEstimateApiArgs {
  priceEstimateApiUrl: string;
  subscriptionId: string;
}

export interface GetAccountDataResponse {
  firstName: string;
  lastName: string;
  email: string;
  customerGuid: string;
}

export interface ResendActivationLinkApiArgs {
  email: string;
  customerGuid: string;
}

const TEN_MINUTES = 600;

const defaultHeaders = {
  'Content-Type': 'application/json',
};

export const api = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getManageCustomers: build.query<ManageCustomersResponse, void>({
      query: () => `/${endpoints.customerApi.manageCustomers}`,
      transformResponse(response: ManageCustomersResponse) {
        if (
          response.organizationCustomers &&
          Array.isArray(response.organizationCustomers) &&
          response.organizationCustomers.length > 0 &&
          typeof response.organizationCustomers[0].customerId === 'number' &&
          Array.isArray(
            response.organizationCustomers[0].assignedActiveSubscriptions,
          )
        ) {
          return response;
        }
        return {
          ...response,
          organizationCustomers: [],
        };
      },
      providesTags: [Cache.Customers],
    }),

    getAvailableSubscriptionRoles: build.query<SubscriptionRole[], void>({
      query: () => `/${endpoints.customerApi.availableSubscriptionRoles}`,
      transformResponse(response: SubscriptionRole[]) {
        if (
          Array.isArray(response) &&
          response.length > 0 &&
          typeof response[0].id === 'number'
        ) {
          return response;
        }
        return [];
      },
    }),

    getOrganizationActiveSubscriptions: build.query<Subscription[], void>({
      query: () => `/${endpoints.customerApi.organizationAvailableSubscriptions}`,
      transformResponse(response: Subscription[]) {
        if (
          Array.isArray(response) &&
          response.length > 0 &&
          response[0]?.subscriptionId &&
          response[0]?.subscriptionName
        ) {
          return response;
        }
        return [];
      },
      providesTags: [Cache.Subscriptions],
    }),

    getOrganizationStatistics: build.query<
      Statistic[],
      GetOrganizationStatisticsApiArgs
    >({
      query: ({ startDate, endDate }) =>
        `/${endpoints.customerApi.organizationStatistics}?startDate=${startDate}&endDate=${endDate}`,
    }),

    addCustomersToSubscriptions: build.mutation<
      AddCustomersToSubscriptionsResponse,
      AddCustomersToSubscriptionsApiArgs & { selectedCustomersCount: number }
    >({
      invalidatesTags: [Cache.Customers, Cache.Subscriptions],
      query: ({ selectedCustomersCount: _, ...queryArg }) => ({
        url: `/${endpoints.customerApi.addCustomersToSubscriptions}`,
        method: 'POST',
        headers: defaultHeaders,
        body: queryArg,
      }),
      onQueryStarted: async ({ selectedCustomersCount }, rtkApi) => {
        const { queryFulfilled, dispatch } = rtkApi;
        const { translations } = rtkApi.getState() as RootState;
        const { data } = await queryFulfilled;
        const { updatedCustomersCount } = data;
        const variant =
          updatedCustomersCount === selectedCustomersCount ? 'success' : 'neutral';

        if (updatedCustomersCount) {
          dispatch(
            showMessage({
              message: getTranslation(
                'Plugins.Misc.SDTheme.Customer.ManageUsers.UsersUpdatedMessage',
                translations,
                updatedCustomersCount,
                selectedCustomersCount,
              ),
              variant,
            }),
          );
        } else {
          dispatch(
            showMessage({
              message: getTranslation(
                'Plugins.Misc.SDTheme.Customer.ManageUsers.NoUsersUpdated',
                translations,
              ),
              variant: 'danger',
            }),
          );
        }
      },
    }),

    removeCustomersFromSubscriptions: build.mutation<
      RemoveCustomersFromSubscriptionsResponse,
      RemoveCustomersFromSubscriptionsApiArgs & { selectedCustomersCount: number }
    >({
      invalidatesTags: [Cache.Customers, Cache.Subscriptions],
      query: (queryArg) => ({
        url: `/${endpoints.customerApi.removeCustomersFromSubscriptions}`,
        method: 'POST',
        headers: defaultHeaders,
        body: queryArg,
      }),
      onQueryStarted: async ({ selectedCustomersCount }, rtkApi) => {
        const { queryFulfilled, dispatch } = rtkApi;
        const { translations } = rtkApi.getState() as RootState;
        const { data } = await queryFulfilled;
        const { updatedCustomersCount } = data;
        const variant =
          updatedCustomersCount === selectedCustomersCount ? 'success' : 'neutral';

        if (updatedCustomersCount) {
          dispatch(
            showMessage({
              message: getTranslation(
                'Plugins.Misc.SDTheme.Customer.ManageUsers.UsersUpdatedMessage',
                translations,
                updatedCustomersCount,
                selectedCustomersCount,
              ),
              variant,
            }),
          );
        } else {
          dispatch(
            showMessage({
              message: getTranslation(
                'Plugins.Misc.SDTheme.Customer.ManageUsers.NoUsersUpdated',
                translations,
              ),
              variant: 'danger',
            }),
          );
        }
      },
    }),

    getCustomerAvailableSubscriptions: build.query<
      SubscriptionOverviewResponse,
      void
    >({
      query: () => `/${endpoints.customerApi.customerAvailableSubscriptions}`,
    }),

    assignSubscriptionCustomerRole: build.mutation<
      AssignSubscriptionCustomerRoleResponse,
      AssignSubscriptionCustomerRoleApiArgs & { selectedCustomersCount: number }
    >({
      invalidatesTags: [Cache.Customers],
      query: (queryArg) => ({
        url: `/${endpoints.customerApi.assignSubscriptionCustomerRole}`,
        method: 'POST',
        headers: defaultHeaders,
        body: queryArg,
      }),
      onQueryStarted: async ({ selectedCustomersCount }, rtkApi) => {
        const { queryFulfilled, dispatch } = rtkApi;
        const { translations } = rtkApi.getState() as RootState;
        const { data } = await queryFulfilled;
        const { updatedCustomersCount } = data;
        const variant =
          updatedCustomersCount === selectedCustomersCount ? 'success' : 'neutral';

        if (updatedCustomersCount) {
          dispatch(
            showMessage({
              message: getTranslation(
                'Plugins.Misc.SDTheme.Customer.ManageUsers.UsersUpdatedMessage',
                translations,
                updatedCustomersCount,
                selectedCustomersCount,
              ),
              variant,
            }),
          );
        } else {
          dispatch(
            showMessage({
              message: getTranslation(
                'Plugins.Misc.SDTheme.Customer.ManageUsers.NoUsersUpdated',
                translations,
              ),
              variant: 'danger',
            }),
          );
        }
      },
    }),

    removeCustomersFromOrganization: build.mutation<
      RemoveCustomersFromOrganizationResponse,
      RemoveCustomersFromOrganizationApiArgs & { selectedCustomersCount: number }
    >({
      invalidatesTags: [Cache.Customers],
      query: (queryArg) => ({
        url: `/${endpoints.customerApi.removeCustomersFromOrganization}`,
        method: 'POST',
        headers: defaultHeaders,
        body: queryArg,
      }),
      onQueryStarted: async ({ selectedCustomersCount }, rtkApi) => {
        const { queryFulfilled, dispatch } = rtkApi;
        const { translations } = rtkApi.getState() as RootState;
        const { data } = await queryFulfilled;
        const { removedCustomersCount } = data;
        const variant =
          removedCustomersCount === selectedCustomersCount ? 'success' : 'neutral';

        if (removedCustomersCount) {
          dispatch(
            showMessage({
              message: getTranslation(
                'Plugins.Misc.SDTheme.Customer.ManageUsers.CustomersRemovedMessage',
                translations,
                removedCustomersCount,
                selectedCustomersCount,
              ),
              variant,
            }),
          );
        } else {
          dispatch(
            showMessage({
              message: getTranslation(
                'Plugins.Misc.SDTheme.Customer.ManageUsers.NoSelectedCustomersRemoved',
                translations,
              ),
              variant: 'danger',
            }),
          );
        }
      },
    }),

    getSubscriptionPriceEstimate: build.mutation<
      unknown,
      GetSubscriptionPriceEstimateApiArgs
    >({
      query: ({ priceEstimateApiUrl, subscriptionId }) => ({
        url: `${priceEstimateApiUrl}?subscriptionId=${subscriptionId}`,
        method: 'GET',
      }),
    }),

    getCompanies: build.query<CompanySearchResponse, CompanySearchArgs>({
      query: ({ companyName }) => ({
        url: `/${endpoints.registration.companySearch}`,
        method: 'GET',
        params: {
          companyName,
        },
      }),
    }),

    getCountries: build.query<CountryResponse, void>({
      providesTags: [Cache.Countries],
      query: () => ({
        url: `/${endpoints.registration.countries}`,
        method: 'GET',
      }),
    }),

    createCompanyWithOrgUser: build.mutation<
      CreateCompanyWithOrgUserResponse,
      CreateCompanyWithOrgUserRequest
    >({
      query: ({ ...bodyArg }) => ({
        url: `/${endpoints.registration.createCompanyWithOrgUser}`,
        method: 'POST',
        body: bodyArg,
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const response = await queryFulfilled;

          const redirectUrl = response.meta?.response?.headers?.get('Location');
          if (redirectUrl) {
            globalThis.location.href = redirectUrl;
          }
        } catch (error: unknown) {
          console.warn(error);
        }
      },
    }),

    createCompanyWithoutOrgUser: build.mutation<
      CreateCompanyWithoutOrgUserResponse,
      CreateCompanyWithoutOrgUserRequest
    >({
      query: ({ ...bodyArg }) => ({
        url: `/${endpoints.registration.createCompanyWithoutOrgUser}`,
        method: 'POST',
        body: bodyArg,
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const response = await queryFulfilled;

          const redirectUrl = response.meta?.response?.headers?.get('Location');
          if (redirectUrl) {
            globalThis.location.href = redirectUrl;
          }
        } catch (error: unknown) {
          console.warn(error);
        }
      },
    }),

    createPrivateUser: build.mutation<
      CreatePrivateUserResponse,
      CreatePrivateUserRequest
    >({
      query: ({ ...bodyArg }) => ({
        url: `/${endpoints.registration.createPrivateUser}`,
        method: 'POST',
        body: bodyArg,
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const response = await queryFulfilled;

          const redirectUrl = response.meta?.response?.headers?.get('Location');
          if (redirectUrl) {
            globalThis.location.href = redirectUrl;
          }
        } catch (error: unknown) {
          console.warn(error);
        }
      },
    }),

    getSchools: build.query<SchoolResponse, void>({
      providesTags: [Cache.Schools],
      query: () => ({
        url: `/${endpoints.registration.schools}`,
        method: 'GET',
      }),
    }),

    createStudentUser: build.mutation<
      CreateStudentUserResponse,
      CreateStudentUserRequest
    >({
      query: ({ ...bodyArg }) => ({
        url: `/${endpoints.registration.createStudentUser}`,
        method: 'POST',
        body: bodyArg,
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const response = await queryFulfilled;

          const redirectUrl = response.meta?.response?.headers?.get('Location');
          if (redirectUrl) {
            globalThis.location.href = redirectUrl;
          }
        } catch (error: unknown) {
          console.warn(error);
        }
      },
    }),

    getFooterInfo: build.query<FooterResponse, void>({
      query: () => `/${endpoints.global.footer}`,
      providesTags: [Cache.Footer],
    }),

    getAccountData: build.query<GetAccountDataResponse, void>({
      query: () => `/${endpoints.registration.account}`,
      providesTags: [Cache.Account],
    }),

    resendActivationLink: build.mutation<unknown, ResendActivationLinkApiArgs>({
      query: ({ ...bodyArgs }) => ({
        url: `/${endpoints.registration.activationlink}`,
        method: 'POST',
        body: bodyArgs,
      }),
      onQueryStarted: async (_, rtkApi) => {
        const { queryFulfilled, dispatch } = rtkApi;
        const { translations } = rtkApi.getState() as RootState;

        try {
          await queryFulfilled;

          dispatch(
            showMessage({
              message: getTranslation(
                'Plugins.Misc.SDTheme.Customer.Registration.ActivationEmailSent',
                translations,
              ),
              variant: 'success',
            }),
          );
        } catch (error: unknown) {
          console.warn(error);
        }
      },
    }),

    search: build.query<SearchResponse, SearchRequest>({
      keepUnusedDataFor: TEN_MINUTES,
      query: ({ ...bodyArg }) => ({
        url: `/${endpoints.global.search}`,
        method: 'POST',
        body: bodyArg,
      }),
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled;

          dispatch(setPage(data.pageInfo.page));
          dispatch(setActiveSort(data.activeSort));
          const selectedPageTypes = data.filters?.pageType?.filter(
            (filterItem) => filterItem.selected,
          );
          const selectedProductTypes = data.filters?.productType?.filter(
            (filterItem) => filterItem.selected,
          );
          const selectedStatuses = data.filters?.statuses?.filter(
            (filterItem) => filterItem.selected,
          );
          const selectedLanguages = data.filters?.languages?.filter(
            (filterItem) => filterItem.selected,
          );
          const selectedPublishers = data.filters?.publishers?.filter(
            (filterItem) => filterItem.selected,
          );
          const selectedSector = data.filters?.sector?.filter(
            (filterItem) => filterItem.selected,
          );

          dispatch(
            updateFiltersFromServer({
              filters: {
                pageType: selectedPageTypes,
                productType: selectedProductTypes,
                statuses: selectedStatuses,
                languages: selectedLanguages,
                publishers: selectedPublishers,
                sector: selectedSector,
              },
            }),
          );
        } catch (error: unknown) {
          console.warn(error);
        }
      },
      transformResponse(response: SearchResponse) {
        const filteredPageTypes = filterVisibleFilterItems(
          response.filters?.pageType,
        );
        const filteredProductTypes = filterVisibleFilterItems(
          response.filters?.productType,
        );
        const filteredStatuses = filterVisibleFilterItems(
          response.filters?.statuses?.filter(
            (item) => (item.value as SearchResultStatus) !== SearchResultStatus.None,
          ),
        );
        const filteredLanguages = filterVisibleFilterItems(
          response.filters?.languages,
        );
        const filteredPublishers = filterVisibleFilterItems(
          response.filters?.publishers,
        );
        const filteredSector = filterVisibleFilterItems(response.filters?.sector);

        return {
          ...response,
          filters: {
            languages: filteredLanguages,
            pageType: filteredPageTypes,
            productType: filteredProductTypes,
            publishers: filteredPublishers,
            sector: filteredSector,
            statuses: filteredStatuses,
          },
        };
      },
      providesTags: [Cache.Search],
    }),
  }),
});

export const {
  useGetManageCustomersQuery,
  useGetAvailableSubscriptionRolesQuery,
  useGetOrganizationActiveSubscriptionsQuery,
  useGetOrganizationStatisticsQuery,
  useAddCustomersToSubscriptionsMutation,
  useRemoveCustomersFromSubscriptionsMutation,
  useGetCustomerAvailableSubscriptionsQuery,
  useAssignSubscriptionCustomerRoleMutation,
  useRemoveCustomersFromOrganizationMutation,
  useGetSubscriptionPriceEstimateMutation,
  useLazyGetCompaniesQuery,
  useGetCountriesQuery,
  useCreateCompanyWithOrgUserMutation,
  useCreateCompanyWithoutOrgUserMutation,
  useCreatePrivateUserMutation,
  useCreateStudentUserMutation,
  useGetSchoolsQuery,
  useGetFooterInfoQuery,
  useGetAccountDataQuery,
  useResendActivationLinkMutation,
  useSearchQuery,
  useLazySearchQuery,
} = api;
